// Dynamic schedule data powered by Ticketmaster for football, with legacy
// providers retained as fallbacks while remaining sports are migrated.
import ScheduleAPI from '../utils/scheduleAPI';
import APISportsScheduleAPI from '../utils/apiSportsScheduleAPI';
import StatsHawkScheduleAPI from '../utils/statsHawkScheduleAPI';
import CFBDScheduleAPI from '../utils/cfbdScheduleAPI';
import TicketmasterScheduleAPI from '../utils/ticketmasterScheduleAPI';

const tryScheduleProviders = async ({ teamName, limit, sport, fullSchedule = false }) => {
  const callProvider = async (provider) => fullSchedule
    ? provider.getTeamSchedule(teamName)
    : provider.getUpcomingGames(teamName, limit);

  // Ticketmaster coverage has been validated for complete college-football and
  // NFL schedules, so prefer it for those sports.
  if (TicketmasterScheduleAPI.supportsTeam(teamName)) {
    try {
      const result = await callProvider(TicketmasterScheduleAPI);
      if (result?.schedule?.length > 0) return result;
      throw new Error(`Ticketmaster returned no usable schedule for ${teamName}`);
    } catch (error) {
      console.warn(`Ticketmaster schedule failed for ${teamName}:`, error);
    }
  }

  // Keep CFBD as a college-football fallback while Ticketmaster is being
  // promoted to production.
  if (CFBDScheduleAPI.supportsTeam(teamName)) {
    try {
      const result = await callProvider(CFBDScheduleAPI);
      if (result?.schedule?.length > 0) return result;
      throw new Error(`CollegeFootballData returned no usable schedule for ${teamName}`);
    } catch (error) {
      console.warn(`CollegeFootballData schedule failed for ${teamName}:`, error);
    }
  }

  if (StatsHawkScheduleAPI.supportsTeam(teamName)) {
    try {
      const result = await callProvider(StatsHawkScheduleAPI);
      if (result?.schedule?.length > 0) return result;
      throw new Error(`StatsHawk returned no usable schedule for ${teamName}`);
    } catch (error) {
      console.warn(`StatsHawk schedule failed for ${teamName}:`, error);
    }
  }

  if (APISportsScheduleAPI.supportsTeam(teamName)) {
    try {
      const result = await callProvider(APISportsScheduleAPI);
      if (result?.schedule?.length > 0) return result;
      throw new Error(`API-Sports returned no usable schedule for ${teamName}`);
    } catch (error) {
      console.warn(`API-Sports schedule failed for ${teamName}:`, error);
    }
  }

  return fullSchedule
    ? ScheduleAPI.getTeamSchedule(teamName, sport)
    : ScheduleAPI.getUpcomingGames(teamName, limit);
};

export const getUpcomingGames = async (teamName, limit = 6) => {
  try {
    const result = await tryScheduleProviders({ teamName, limit, fullSchedule: false });
    if (!result) {
      console.warn(`No schedule data found for team: ${teamName}`);
      return null;
    }
    return result;
  } catch (error) {
    console.error(`Error fetching schedule for ${teamName}:`, error);
    throw error;
  }
};

export const getTeamSchedule = async (teamName, sport = 'auto') => {
  try {
    const result = await tryScheduleProviders({ teamName, sport, fullSchedule: true });
    if (!result) {
      console.warn(`No schedule data found for team: ${teamName}`);
      return null;
    }
    return result;
  } catch (error) {
    console.error(`Error fetching team schedule for ${teamName}:`, error);
    throw error;
  }
};

export const clearScheduleCache = () => {
  TicketmasterScheduleAPI.clearCache();
  CFBDScheduleAPI.clearCache();
  StatsHawkScheduleAPI.clearCache();
  APISportsScheduleAPI.clearCache();
  ScheduleAPI.clearCache();
};

export const getScheduleCacheInfo = () => ({
  size:
    TicketmasterScheduleAPI.getCacheSize()
    + CFBDScheduleAPI.getCacheSize()
    + StatsHawkScheduleAPI.getCacheSize()
    + APISportsScheduleAPI.getCacheSize()
    + ScheduleAPI.getCacheSize(),
  duration: '30 minutes',
});

export const preloadTeamSchedules = async (teamNames) => {
  const promises = teamNames.map(teamName =>
    getUpcomingGames(teamName, 6).catch(error => {
      console.warn(`Failed to preload schedule for ${teamName}:`, error);
      return null;
    })
  );

  const results = await Promise.all(promises);
  const successCount = results.filter(result => result !== null).length;
  console.log(`Preloaded schedules for ${successCount}/${teamNames.length} teams`);
  return results;
};

export {
  ScheduleAPI,
  APISportsScheduleAPI,
  StatsHawkScheduleAPI,
  CFBDScheduleAPI,
  TicketmasterScheduleAPI,
};
